import Link from '@/components/client/seo/link'
import type { Locale } from '@/i18n'
import SvgArrowRight from '@/icons/regular/ArrowRight'
import { formatDate } from '@/lib/utils/date-utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { StoryCardArt } from './story-card-art'
import { type StoryCardStory, type StoryCardTracking, trackingAttributes } from './types'

interface StoryCardCompactProps {
  story: StoryCardStory
  locale: Locale
  origin: string
  tracking: StoryCardTracking
  /**
   * Show the story's description under the headline. Explicitly opt-in, NOT
   * inferred from whether `metaDescription` happens to be set: the author
   * archive's declared row type omits the field but it is handed full story
   * objects at runtime, so a data-driven check silently added a description to
   * a list that is deliberately a dense two-line index.
   */
  showDescription?: boolean
}

/**
 * The site's secondary story card: thumbnail left, headline right, on a
 * translucent white tile. Used for archive lists and for the stories stacked
 * beside a `StoryCardFeatured`.
 */
export const StoryCardCompact = ({
  story,
  locale,
  origin,
  tracking,
  showDescription = false,
}: StoryCardCompactProps) => {
  const date = story.republishedAt ?? story.publishedAt

  return (
    <article className="group/story border-navy/10 rounded-2xl border bg-white/75 p-3 shadow-xs transition-shadow hover:shadow-sm md:p-4">
      <Link
        href={localizedHref(locale, `/${story.fullSlug}`)}
        prefetch={false}
        rel={story.noindex ? 'nofollow' : undefined}
        {...trackingAttributes(tracking)}
        className="grid h-full grid-cols-[7rem_minmax(0,1fr)] gap-4 md:grid-cols-[8.5rem_minmax(0,1fr)]"
      >
        <StoryCardArt story={story} origin={origin} sizes="(min-width: 768px) 136px, 112px" compact />
        <div className="flex min-w-0 flex-col py-1">
          <time className="text-navy/45 text-[0.72rem] font-medium" dateTime={date}>
            {formatDate(date, locale)}
          </time>
          <h3 className="text-navy group-hover/story:text-lapis mt-2 line-clamp-3 text-[1rem] leading-5 font-semibold tracking-[-0.015em] text-pretty transition-colors md:text-[1.08rem] md:leading-[1.4]">
            {story.title}
          </h3>
          {showDescription && story.metaDescription ? (
            <p className="text-navy/55 mt-2 line-clamp-2 text-[0.82rem] leading-5 text-pretty">
              {story.metaDescription}
            </p>
          ) : null}
          <SvgArrowRight aria-hidden className="text-navy/35 mt-auto h-3.5 w-3.5 self-end" />
        </div>
      </Link>
    </article>
  )
}
